import React from 'react';
import { NotificationItem } from '../types/notifications.types';
import { Button, Badge } from '@/components/ui';
import { X, User, Calendar, Clock, FileText, Paperclip } from 'lucide-react';
import { format } from 'date-fns';
import { vi } from 'date-fns/locale';
import { getRoleBadgeClasses } from '@/utils/role';

interface NotificationDetailPanelProps {
  item: NotificationItem;
  onClose: () => void;
}

export function NotificationDetailPanel({ item, onClose }: NotificationDetailPanelProps) {
  if (!item) return null;

  const isUnread = item.status_code === 'unread';

  return (
    <div className="flex flex-col h-full bg-white rounded-2xl shadow-sm border border-slate-200">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-slate-100">
        <h3 className="text-lg text-slate-900 truncate pr-4">{item.title}</h3>
        <Button variant="ghost" size="icon" onClick={onClose} className="shrink-0 text-slate-400 hover:text-slate-600">
          <X className="w-5 h-5" />
        </Button>
      </div>

      <div className="p-4 flex-1 overflow-y-auto space-y-6">
        {/* Tags */}
        <div className="flex flex-wrap gap-2">
          {item.entity_id && (
            <Badge variant="outline" className="text-blue-600 bg-blue-50 border-blue-200">
              {item.entity_id}
            </Badge>
          )}
          {isUnread ? (
            <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 border-0">Chưa đọc</Badge>
          ) : (
            <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100 border-0">Đã đọc</Badge>
          )}
        </div>

        {/* Details List */}
        <div className="space-y-4">
          {item.project && (
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <FileText className="w-4 h-4" />
                <span>Dự án</span>
              </div>
              <p className="sm:text-sm text-xs text-slate-900 pl-6">{item.project.name}</p>
            </div>
          )}

          <div className="space-y-1">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <User className="w-4 h-4" />
              <span>Người gửi</span>
            </div>
            <div className="flex items-center gap-2 pl-6">
              <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 sm:text-sm text-xs">
                HT
              </div>
              <div>
                <p className="sm:text-sm text-xs text-slate-900">Hệ thống</p>
                <p className="text-[10px] text-slate-500">Tự động</p>
              </div>
            </div>
          </div>

          <div className="space-y-1">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Clock className="w-4 h-4" />
              <span>Thời gian gửi</span>
            </div>
            <p className="sm:text-sm text-xs text-slate-900 pl-6">
              {format(new Date(item.created_at), 'dd/MM/yyyy HH:mm')}
            </p>
          </div>

          {/* Content */}
          <div className="space-y-2 pt-2 border-t border-slate-100">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <FileText className="w-4 h-4" />
              <span>Nội dung</span>
            </div>
            <div className="bg-slate-50 p-3 rounded-md sm:text-sm text-xs text-slate-700 whitespace-pre-wrap">
              {item.message}
            </div>
          </div>
        </div>
      </div>

      {/* Action footer */}
      {item.link && (
        <div className="p-4 border-t border-slate-100">
          <Button className="w-full bg-emerald-700 hover:bg-emerald-800 text-white rounded-md p-3" onClick={() => window.location.href = item.link!}>
            Xem chi tiết yêu cầu
          </Button>
        </div>
      )}
    </div>
  );
}
